/**
 * The clause library — one surface over the two libraries the backend keeps.
 *
 * The organisation library (`/clause`) is org-scoped and writable by an org
 * admin; the GAIA library (`/gaia-clause`) is global, readable by anyone and
 * writable only by a platform admin. That asymmetry is real, and it is the
 * reason this module exists: one place picks the endpoint pair and the
 * permission, so callers stay library-agnostic for everything but contract
 * types. Those are the one column that differs: an organisation clause holds
 * the org's contract types (`ContractTypeRef`s, written by uid), a GAIA clause
 * holds code template keys — which is why writes take a per-library body.
 */
import {
  createClause,
  createGaiaClause,
  deleteClause,
  deleteGaiaClause,
  getClause,
  getGaiaClause,
  searchClause,
  searchGaiaClause,
  updateClause,
  updateGaiaClause,
  type ClauseCreate,
  type ClauseInDb,
  type ClauseSearchQuery,
  type ClauseUpdate,
  type GaiaClauseCreate,
  type GaiaClauseInDb,
  type GaiaClauseUpdate,
} from '@/api';
import { extractErrorDetail } from '@/utils/errorHandler';

/** Which of the two libraries a call addresses. */
export type ClauseLibrary = 'gaia' | 'organisation';

/** A clause from either library. */
export type LibraryClause = ClauseInDb | GaiaClauseInDb;

/** A create body, tagged with the library whose shape it has. */
export type ClauseCreateWrite =
  { library: 'organisation'; body: ClauseCreate } | { library: 'gaia'; body: GaiaClauseCreate };

/** An update body, tagged with the library whose shape it has. */
export type ClauseUpdateWrite =
  { library: 'organisation'; body: ClauseUpdate } | { library: 'gaia'; body: GaiaClauseUpdate };

/** The permission fields a write gate reads. */
export interface ClauseWritePermissions {
  is_org_admin: boolean;
  is_platform_admin: boolean;
}

export interface ClauseSearchParams {
  library: ClauseLibrary;
  text?: string;
  page?: number;
  pageSize?: number;
  sortKey?: string;
  sortDirection?: 'asc' | 'desc';
  columnFilters?: Record<string, string[]>;
}

export interface ClauseSearchPage {
  results: LibraryClause[];
  result_count: number;
  filter_options: Record<string, unknown[]>;
}

/**
 * The API caps `limit` at 50, so no caller may offer a larger page size — the
 * old library offered 100 and every such request came back a 422.
 */
export const CLAUSE_PAGE_SIZE_LIMIT = 50;

/** The page sizes the library offers, all within the API's cap. */
export const CLAUSE_PAGE_SIZES = [10, 25, 50];

const DEFAULT_PAGE_SIZE = 10;

/** The library's search is fuzzy; this is the threshold it has always used. */
const SIMILARITY_THRESHOLD = 0.5;

/**
 * Who may create, edit and delete in a library.
 *
 * The GAIA library takes a platform admin — the old page gated it on
 * `is_org_admin`, which rendered buttons whose every click came back a 403.
 */
export function canWriteClauses(
  library: ClauseLibrary,
  permissions: ClauseWritePermissions
): boolean {
  return library === 'gaia' ? permissions.is_platform_admin : permissions.is_org_admin;
}

/**
 * The active filter record from `GaiaFilterPanel` narrowed to the
 * `{ field: values[] }` the search endpoint takes.
 *
 * Empty columns are dropped rather than sent as `[]`, which the backend would
 * read as "match nothing".
 */
export function buildColumnFilters(active: Record<string, unknown>): Record<string, string[]> {
  const filters: Record<string, string[]> = {};
  for (const [field, raw] of Object.entries(active)) {
    const values = (Array.isArray(raw) ? raw : [raw])
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(value => String(value));
    if (values.length > 0) filters[field] = values;
  }
  return filters;
}

/**
 * The filter options from a search response, ready for `GaiaFilterPanel`.
 *
 * `contract_types` is an array column, so its distinct values arrive as arrays
 * and have to be flattened. Nulls are dropped here: the panel would render one
 * as its `(Empty)` sentinel, and no `column_filters` value can express "is
 * null", so the option could only ever no-op.
 */
export function normalizeFilterOptions(
  raw: Record<string, unknown[]> | undefined
): Record<string, string[]> {
  const options: Record<string, string[]> = {};
  for (const [field, values] of Object.entries(raw ?? {})) {
    const flat = values
      .flat()
      .filter(value => value !== null && value !== undefined && value !== '')
      .map(value => String(value));
    options[field] = [...new Set(flat)].sort((a, b) => a.localeCompare(b));
  }
  return options;
}

function searchBody(params: ClauseSearchParams): ClauseSearchQuery {
  const pageSize = params.pageSize ?? DEFAULT_PAGE_SIZE;
  return {
    text: params.text ?? '',
    limit: Math.min(pageSize, CLAUSE_PAGE_SIZE_LIMIT),
    offset: params.page ? (params.page - 1) * pageSize : 0,
    sort_key: params.sortKey ?? 'created_at',
    sort_direction: params.sortDirection ?? 'desc',
    similarity_threshold: SIMILARITY_THRESHOLD,
    column_filters: params.columnFilters ?? {},
  };
}

export const ClauseLibraryService = {
  /** One page of a library, with its total and its filter options. */
  async search(params: ClauseSearchParams): Promise<ClauseSearchPage> {
    const body = searchBody(params);
    try {
      const response =
        params.library === 'gaia' ? await searchGaiaClause({ body }) : await searchClause({ body });
      if (!response.data) throw new Error('The clause search returned no data.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load clauses. Please try again.'));
    }
  },

  /**
   * How many clauses a library holds, for the tab count pills.
   *
   * `limit: 0` is a valid query and returns the count without a page of rows.
   */
  async count(library: ClauseLibrary): Promise<number> {
    const page = await this.search({ library, pageSize: 0 });
    return page.result_count;
  },

  /** One clause by uid — what a deep link into the drawer resolves through. */
  async get(library: ClauseLibrary, uid: string): Promise<LibraryClause> {
    try {
      const response =
        library === 'gaia'
          ? await getGaiaClause({ path: { gaia_clause_uid: uid } })
          : await getClause({ path: { clause_uid: uid } });
      if (!response.data) throw new Error('The clause was not found.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to load the clause.'));
    }
  },

  async create(write: ClauseCreateWrite): Promise<LibraryClause> {
    try {
      const response =
        write.library === 'gaia'
          ? await createGaiaClause({ body: write.body })
          : await createClause({ body: write.body });
      if (!response.data) throw new Error('The clause was not created.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to create the clause.'));
    }
  },

  async update(uid: string, write: ClauseUpdateWrite): Promise<LibraryClause> {
    try {
      const response =
        write.library === 'gaia'
          ? await updateGaiaClause({ path: { gaia_clause_uid: uid }, body: write.body })
          : await updateClause({ path: { clause_uid: uid }, body: write.body });
      if (!response.data) throw new Error('The clause was not updated.');
      return response.data;
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to save the clause.'));
    }
  },

  async remove(library: ClauseLibrary, uid: string): Promise<void> {
    try {
      if (library === 'gaia') {
        await deleteGaiaClause({ path: { gaia_clause_uid: uid } });
      } else {
        await deleteClause({ path: { clause_uid: uid } });
      }
    } catch (error) {
      throw new Error(extractErrorDetail(error, 'Failed to delete the clause.'));
    }
  },
};
